import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Bell, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  FileText, 
  Image as ImageIcon,
  Download,
  X,
  Calendar,
  AlertCircle,
  Palette,
  Upload,
  MessageSquare,
  Send
} from 'lucide-react';
import { announcementsService } from '../services/announcementsService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

// Komponent licznika czasu do wydarzenia
const EventCountdown = ({ eventDate }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!eventDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const event = new Date(eventDate);
      const difference = event - now;

      if (difference <= 0) {
        setTimeLeft({ expired: true });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds, expired: false });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [eventDate]);

  if (!eventDate || !timeLeft) return null;

  if (timeLeft.expired) {
    return (
      <div className="bg-red-100 border border-red-300 rounded-lg p-3 mt-3">
        <div className="flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span className="font-semibold">Wydarzenie już się odbyło</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mt-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center text-blue-700">
          <Clock className="h-5 w-5 mr-2" />
          <span className="font-semibold">Pozostało do wydarzenia:</span>
        </div>
        <div className="flex items-center space-x-4 text-blue-800 font-bold">
          {timeLeft.days > 0 && (
            <div className="text-center">
              <div className="text-2xl">{timeLeft.days}</div>
              <div className="text-xs">dni</div>
            </div>
          )}
          <div className="text-center">
            <div className="text-2xl">{String(timeLeft.hours).padStart(2, '0')}</div>
            <div className="text-xs">godz</div>
          </div>
          <div className="text-center">
            <div className="text-2xl">{String(timeLeft.minutes).padStart(2, '0')}</div>
            <div className="text-xs">min</div>
          </div>
          <div className="text-center">
            <div className="text-2xl">{String(timeLeft.seconds).padStart(2, '0')}</div>
            <div className="text-xs">sek</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Komponent kolorowanki
const ColoringPage = ({ coloringPage, uploadsUrl }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-purple-600 hover:text-purple-700 font-medium"
      >
        <Palette className="h-5 w-5" />
        <span>{coloringPage.title || 'Kolorowanka'}</span>
      </button>
      {isOpen && (
        <div className="mt-2 border border-purple-200 rounded-lg p-4 bg-purple-50">
          <img 
            src={`${uploadsUrl}/uploads/${coloringPage.imageUrl}`} 
            alt={coloringPage.title || 'Kolorowanka'}
            className="max-w-full h-auto rounded-lg shadow-md"
          />
        </div>
      )}
    </div>
  );
};

const Announcements = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'informacja',
    eventDate: '',
    isImportant: false,
    isForAll: true,
    targetUsers: [],
    attachments: [],
    holidayBackground: null,
    coloringPages: []
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [showConfirmations, setShowConfirmations] = useState({});
  const [confirmationsData, setConfirmationsData] = useState({});
  const [newComments, setNewComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL || 'https://farm.my-it.pl/api';
  const uploadsUrl = apiUrl.replace('/api', '');

  // Sprawdź czy użytkownik ma uprawnienia do dodawania ogłoszeń
  const canAddAnnouncements = user?.role === 'super admin' || user?.moduleAccess?.announcements === true;

  // Pobierz listę użytkowników (jeśli użytkownik ma uprawnienia)
  const { data: usersData } = useQuery(
    'announcements-users-list',
    async () => {
      if (canAddAnnouncements) {
        try {
          const response = await announcementsService.getUsers();
          return response.users || [];
        } catch (error) {
          console.error('Błąd pobierania użytkowników:', error);
          return [];
        }
      }
      return [];
    },
    { enabled: canAddAnnouncements }
  );

  useEffect(() => {
    if (usersData) {
      setAvailableUsers(usersData);
    }
  }, [usersData]);

  // Zamknij dropdown użytkowników po kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserSelect && !event.target.closest('.user-select-container')) {
        setShowUserSelect(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserSelect]);

  // Pobierz ogłoszenia
  const { data, isLoading, error } = useQuery(
    ['announcements', selectedYear],
    () => announcementsService.getAnnouncements(selectedYear),
    { refetchInterval: 60000 } // Odświeżaj co minutę
  );

  const announcements = data?.announcements || [];

  // Sprawdź które ogłoszenia mają nowe komentarze (po ostatnim przeczytaniu)
  useEffect(() => {
    console.log('🔍 Sprawdzam nowe komentarze dla', announcements.length, 'ogłoszeń');
    const newCommentsMap = {};
    
    announcements.forEach(announcement => {
      const hasComments = announcement.comments && announcement.comments.length > 0;
      console.log(`📢 Ogłoszenie "${announcement.title}":`, {
        hasComments,
        commentsCount: announcement.comments?.length || 0,
        comments: announcement.comments
      });
      
      if (hasComments) {
        const readStatus = announcement.readStatus || { read: false, readAt: null };
        console.log(`  Status przeczytania:`, readStatus);
        
        if (readStatus.read && readStatus.readAt) {
          // Sprawdź czy są komentarze dodane po przeczytaniu
          const lastReadDate = new Date(readStatus.readAt);
          console.log(`  Data ostatniego przeczytania:`, lastReadDate);
          
          const hasNewComments = announcement.comments.some(comment => {
            if (!comment.createdAt) {
              console.log(`  ⚠️ Komentarz bez daty:`, comment);
              return false;
            }
            const commentDate = new Date(comment.createdAt);
            const isNew = commentDate > lastReadDate;
            console.log(`  Komentarz z ${commentDate.toISOString()}:`, isNew ? 'NOWY' : 'stary');
            return isNew;
          });
          
          if (hasNewComments) {
            console.log(`  ✅ OGŁOSZENIE MA NOWE KOMENTARZE!`);
            newCommentsMap[announcement._id] = true;
          }
        } else if (announcement.comments.length > 0) {
          // Jeśli nie przeczytane, wszystkie komentarze są "nowe"
          console.log(`  ✅ Ogłoszenie nieprzeczytane - wszystkie komentarze są nowe`);
          newCommentsMap[announcement._id] = true;
        }
      }
    });
    
    console.log('📊 Mapa nowych komentarzy:', newCommentsMap);
    setNewComments(newCommentsMap);
  }, [announcements]);

  // Mutacje
  const createMutation = useMutation(announcementsService.createAnnouncement, {
    onSuccess: () => {
      toast.success('Ogłoszenie zostało dodane');
      queryClient.invalidateQueries(['announcements']);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas dodawania ogłoszenia');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => announcementsService.updateAnnouncement(id, data),
    {
      onSuccess: () => {
        toast.success('Ogłoszenie zostało zaktualizowane');
        queryClient.invalidateQueries(['announcements']);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji ogłoszenia');
      }
    }
  );

  const deleteMutation = useMutation(announcementsService.deleteAnnouncement, {
    onSuccess: () => {
      toast.success('Ogłoszenie zostało usunięte');
      queryClient.invalidateQueries(['announcements']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania ogłoszenia');
    }
  });

  const markAsReadMutation = useMutation(announcementsService.markAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['announcements']);
    }
  });

  const confirmReadMutation = useMutation(announcementsService.confirmRead, {
    onSuccess: (data, announcementId) => {
      toast.success('Zapoznanie się z ogłoszeniem zostało potwierdzone');
      queryClient.invalidateQueries(['announcements']);
      // Odśwież dane potwierdzeń jeśli były już załadowane
      if (showConfirmations[announcementId] && confirmationsData[announcementId]) {
        handleShowConfirmations(announcementId);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas potwierdzania');
    }
  });

  const addCommentMutation = useMutation(
    ({ announcementId, text }) => announcementsService.addComment(announcementId, text),
    {
      onSuccess: () => {
        toast.success('Komentarz został dodany');
        queryClient.invalidateQueries(['announcements']);
        // Wyczyść pole tekstowe
        setCommentTexts({});
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas dodawania komentarza');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: 'informacja',
      eventDate: '',
      isImportant: false,
      isForAll: true,
      targetUsers: [],
      attachments: [],
      holidayBackground: null,
      coloringPages: []
    });
    setEditingAnnouncement(null);
    setShowForm(false);
    setShowUserSelect(false);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    const eventDateValue = announcement.eventDate 
      ? new Date(announcement.eventDate).toISOString().slice(0, 16)
      : '';
    
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      eventDate: eventDateValue,
      isImportant: announcement.isImportant,
      isForAll: announcement.isForAll !== undefined ? announcement.isForAll : true,
      targetUsers: announcement.targetUsers 
        ? announcement.targetUsers.map(u => typeof u === 'object' ? u._id : u)
        : [],
      attachments: [],
      holidayBackground: null,
      coloringPages: []
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingAnnouncement) {
      updateMutation.mutate({
        id: editingAnnouncement._id,
        data: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleFileChange = (field, files) => {
    if (field === 'attachments' || field === 'coloringPages') {
      setFormData(prev => ({
        ...prev,
        [field]: Array.from(files)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: files[0] || null
      }));
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Czy na pewno chcesz usunąć to ogłoszenie?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleMarkAsRead = (id) => {
    markAsReadMutation.mutate(id);
  };

  const handleConfirmRead = async (id) => {
    try {
      await confirmReadMutation.mutateAsync(id);
      // Odśwież dane potwierdzeń po potwierdzeniu
      queryClient.invalidateQueries(['announcements']);
      if (showConfirmations[id]) {
        // Odśwież listę potwierdzeń
        setTimeout(async () => {
          try {
            const response = await announcementsService.getConfirmations(id);
            if (response.success) {
              setConfirmationsData({ ...confirmationsData, [id]: response });
            }
          } catch (error) {
            console.error('Błąd podczas odświeżania potwierdzeń:', error);
          }
        }, 500);
      }
    } catch (error) {
      // Błąd już obsłużony w mutation
    }
  };

  const handleDownloadAttachment = async (announcementId, attachmentIndex, filename) => {
    try {
      const blob = await announcementsService.getAttachment(announcementId, attachmentIndex);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Błąd podczas pobierania załącznika');
    }
  };

  const handleShowConfirmations = async (announcementId) => {
    if (showConfirmations[announcementId]) {
      setShowConfirmations({ ...showConfirmations, [announcementId]: false });
      return;
    }

    try {
      const response = await announcementsService.getConfirmations(announcementId);
      if (response.success) {
        setConfirmationsData({ ...confirmationsData, [announcementId]: response });
        setShowConfirmations({ ...showConfirmations, [announcementId]: true });
      } else {
        toast.error(response.message || 'Błąd podczas pobierania potwierdzeń');
      }
    } catch (error) {
      console.error('Błąd podczas pobierania potwierdzeń:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas pobierania potwierdzeń');
    }
  };

  // Generuj listę lat (od bieżącego roku wstecz)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Błąd podczas ładowania ogłoszeń</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {canAddAnnouncements && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>{showForm ? 'Anuluj' : 'Dodaj ogłoszenie'}</span>
          </button>
        </div>
      )}

      {/* Filtr roku */}
      <div className="flex items-center space-x-4">
        <label className="text-sm font-medium text-gray-700">Rok:</label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Formularz dodawania/edycji */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-semibold mb-4">
            {editingAnnouncement ? 'Edytuj ogłoszenie' : 'Nowe ogłoszenie'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tytuł *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ ogłoszenia
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="informacja">Informacja</option>
                <option value="prawne">Prawne</option>
                <option value="ksiegowe">Księgowe</option>
                <option value="wydarzenie">Wydarzenie</option>
                <option value="inne">Inne</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Treść *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data wydarzenia (dla licznika czasu)
              </label>
              <input
                type="datetime-local"
                value={formData.eventDate}
                onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isImportant"
                checked={formData.isImportant}
                onChange={(e) => setFormData({ ...formData, isImportant: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isImportant" className="ml-2 text-sm text-gray-700">
                Ogłoszenie wymaga potwierdzenia przeczytania
              </label>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Odbiorcy ogłoszenia
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="isForAll"
                    name="recipients"
                    checked={formData.isForAll}
                    onChange={() => setFormData({ ...formData, isForAll: true, targetUsers: [] })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <label htmlFor="isForAll" className="ml-2 text-sm text-gray-700">
                    Dla wszystkich użytkowników
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="isForSelected"
                    name="recipients"
                    checked={!formData.isForAll}
                    onChange={() => setFormData({ ...formData, isForAll: false })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <label htmlFor="isForSelected" className="ml-2 text-sm text-gray-700">
                    Dla wybranych użytkowników
                  </label>
                </div>
                {!formData.isForAll && (
                  <div className="ml-6 mt-2">
                    <div className="relative user-select-container">
                      <button
                        type="button"
                        onClick={() => setShowUserSelect(!showUserSelect)}
                        className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {formData.targetUsers.length > 0
                          ? `Wybrano: ${formData.targetUsers.length} użytkowników`
                          : 'Wybierz użytkowników...'}
                      </button>
                      {showUserSelect && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {availableUsers.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Ładowanie użytkowników...
                            </div>
                          ) : (
                            availableUsers.map((user) => {
                            const isSelected = formData.targetUsers.includes(user._id);
                            return (
                              <label
                                key={user._id}
                                className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        targetUsers: [...formData.targetUsers, user._id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        targetUsers: formData.targetUsers.filter(id => id !== user._id)
                                      });
                                    }
                                  }}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  {user.firstName && user.lastName
                                    ? `${user.firstName} ${user.lastName}`
                                    : user.username}
                                  {user.email && <span className="text-gray-500 ml-2">({user.email})</span>}
                                </span>
                              </label>
                            );
                          }))}
                        </div>
                      )}
                    </div>
                    {formData.targetUsers.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formData.targetUsers.map((userId) => {
                          const user = availableUsers.find(u => u._id === userId);
                          if (!user) return null;
                          return (
                            <span
                              key={userId}
                              className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-lg"
                            >
                              {user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user.username}
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    targetUsers: formData.targetUsers.filter(id => id !== userId)
                                  });
                                }}
                                className="ml-1 text-primary-700 hover:text-primary-900"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Załączniki
              </label>
              <input
                type="file"
                multiple
                onChange={(e) => handleFileChange('attachments', e.target.files)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {formData.attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {Array.from(formData.attachments).map((file, index) => (
                    <div key={index} className="flex items-center justify-between text-sm text-gray-600">
                      <span>{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tło ogłoszenia
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange('holidayBackground', e.target.files)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kolorowanki
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileChange('coloringPages', e.target.files)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {formData.coloringPages.length > 0 && (
                <div className="mt-2 space-y-1">
                  {Array.from(formData.coloringPages).map((file, index) => (
                    <div key={index} className="flex items-center justify-between text-sm text-gray-600">
                      <span>{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={createMutation.isLoading || updateMutation.isLoading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Zapisywanie...' : 'Zapisz'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista ogłoszeń */}
      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Brak ogłoszeń dla wybranego roku</p>
          </div>
        ) : (
          announcements.map((announcement) => {
            const readStatus = announcement.readStatus || { read: false, confirmed: false };
            const isUnread = !readStatus.read;
            const needsConfirmation = announcement.isImportant && !readStatus.confirmed;
            // Sprawdź czy użytkownik jest autorem - porównaj jako stringi dla niezawodności
            // createdBy jest populowane jako obiekt z _id przez backend
            let createdById = null;
            if (announcement.createdBy) {
              // Backend populuje createdBy jako obiekt z _id
              if (announcement.createdBy._id) {
                createdById = String(announcement.createdBy._id);
              } else if (typeof announcement.createdBy === 'string') {
                createdById = announcement.createdBy;
              } else {
                // Fallback dla ObjectId
                createdById = String(announcement.createdBy);
              }
            }
            
            const userId = user?._id ? String(user._id) : null;
            
            // Porównaj jako stringi - MUSI być dokładne dopasowanie
            const isAuthor = createdById !== null && userId !== null && createdById === userId;
            
            // Debug log - zawsze pokazuj w konsoli dla debugowania
            console.log('🔍 Author check:', {
              announcementTitle: announcement.title,
              createdById,
              userId,
              isAuthor,
              canAddAnnouncements,
              willShowButtons: canAddAnnouncements && isAuthor,
              createdByFull: announcement.createdBy
            });
            const isSuperAdmin = user?.role === 'super admin';
            const canViewConfirmations = (isAuthor || isSuperAdmin) && announcement.isImportant;
            const confirmationStats = announcement.confirmationStats;

            const hasNewComments = newComments[announcement._id] || false;
            
            // Debug log dla każdego ogłoszenia
            if (hasNewComments) {
              console.log(`✨ Ogłoszenie "${announcement.title}" MA NOWE KOMENTARZE - powinno migać!`);
            }

            return (
              <div
                key={announcement._id}
                className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                  needsConfirmation 
                    ? 'border-red-500' 
                    : isUnread 
                    ? 'border-blue-500' 
                    : hasNewComments 
                    ? 'border-yellow-500 announcement-blink' 
                    : 'border-gray-300'
                } ${hasNewComments ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''}`}
                style={
                  announcement.holidayBackground?.imageUrl
                    ? {
                        backgroundImage: `url(${uploadsUrl}/uploads/${announcement.holidayBackground.imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }
                    : {}
                }
              >
                <div className={`${announcement.holidayBackground?.imageUrl ? 'bg-white bg-opacity-90 p-4 rounded-lg' : ''}`}>
                  {/* Nagłówek ogłoszenia */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{announcement.title}</h3>
                        {needsConfirmation && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                            Wymaga potwierdzenia
                          </span>
                        )}
                        {isUnread && !needsConfirmation && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                            Nowe
                          </span>
                        )}
                        {hasNewComments && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded flex items-center">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Nowe komentarze
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="capitalize">{announcement.type}</span>
                        <span>•</span>
                        <span>
                          {new Date(announcement.createdAt).toLocaleDateString('pl-PL', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {announcement.createdBy && (
                          <>
                            <span>•</span>
                            <span>
                              {announcement.createdBy.firstName && announcement.createdBy.lastName
                                ? `${announcement.createdBy.firstName} ${announcement.createdBy.lastName}`
                                : announcement.createdBy.username}
                            </span>
                          </>
                        )}
                        <span>•</span>
                        <span className="flex items-center">
                          {announcement.isForAll ? (
                            <span className="text-green-600">Dla wszystkich</span>
                          ) : (
                            <span className="text-blue-600">
                              Dla wybranych ({announcement.targetUsers?.length || 0})
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    {canAddAnnouncements && isAuthor && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(announcement)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edytuj ogłoszenie"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(announcement._id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Usuń ogłoszenie"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Treść ogłoszenia */}
                  <div className="mb-4 text-gray-700 whitespace-pre-wrap">
                    {announcement.content}
                  </div>

                  {/* Licznik czasu do wydarzenia */}
                  {announcement.eventDate && (
                    <EventCountdown eventDate={announcement.eventDate} />
                  )}

                  {/* Kolorowanki */}
                  {announcement.coloringPages && announcement.coloringPages.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                        <Palette className="h-5 w-5 mr-2" />
                        Kolorowanki
                      </h4>
                      {announcement.coloringPages.map((coloringPage, index) => (
                        <ColoringPage
                          key={index}
                          coloringPage={coloringPage}
                          uploadsUrl={uploadsUrl}
                        />
                      ))}
                    </div>
                  )}

                  {/* Załączniki */}
                  {announcement.attachments && announcement.attachments.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <FileText className="h-5 w-5 mr-2" />
                        Załączniki
                      </h4>
                      <div className="space-y-2">
                        {announcement.attachments.map((attachment, index) => (
                          <button
                            key={index}
                            onClick={() => handleDownloadAttachment(announcement._id, index, attachment.originalName)}
                            className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors w-full text-left"
                          >
                            <Download className="h-4 w-4 text-gray-600" />
                            <span className="text-sm text-gray-700">{attachment.originalName}</span>
                            <span className="text-xs text-gray-500 ml-auto">
                              {(attachment.size / 1024).toFixed(2)} KB
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Statystyki potwierdzeń (dla autora/super admina) */}
                  {canViewConfirmations && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-blue-900 flex items-center">
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Statystyki potwierdzeń
                        </h4>
                        <button
                          onClick={() => handleShowConfirmations(announcement._id)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {showConfirmations[announcement._id] ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                        </button>
                      </div>
                      {(confirmationStats || confirmationsData[announcement._id]?.stats) ? (
                        <>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <div className="text-gray-600">Wszyscy odbiorcy</div>
                              <div className="text-lg font-bold text-gray-900">
                                {confirmationsData[announcement._id]?.stats?.total || confirmationStats?.total || 0}
                              </div>
                            </div>
                            <div>
                              <div className="text-green-600">Potwierdzili</div>
                              <div className="text-lg font-bold text-green-700">
                                {confirmationsData[announcement._id]?.stats?.confirmed || confirmationStats?.confirmed || 0}
                              </div>
                            </div>
                            <div>
                              <div className="text-red-600">Nie potwierdzili</div>
                              <div className="text-lg font-bold text-red-700">
                                {confirmationsData[announcement._id]?.stats?.notConfirmed || confirmationStats?.notConfirmed || 0}
                              </div>
                            </div>
                          </div>
                          {showConfirmations[announcement._id] && confirmationsData[announcement._id] && (
                            <div className="mt-4 space-y-3">
                              {confirmationsData[announcement._id].confirmedUsers.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-green-700 mb-2">Potwierdzili ({confirmationsData[announcement._id].confirmedUsers.length}):</h5>
                                  <div className="space-y-1">
                                    {confirmationsData[announcement._id].confirmedUsers.map((item, index) => (
                                      <div key={index} className="flex items-center justify-between px-3 py-2 bg-green-50 rounded-lg">
                                        <span className="text-sm text-gray-700">
                                          {item.user.firstName && item.user.lastName
                                            ? `${item.user.firstName} ${item.user.lastName}`
                                            : item.user.username}
                                          {item.user.email && <span className="text-gray-500 ml-2">({item.user.email})</span>}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {item.confirmedAt ? new Date(item.confirmedAt).toLocaleString('pl-PL') : ''}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {confirmationsData[announcement._id].notConfirmedUsers.length > 0 && (
                                <div>
                                  <h5 className="font-semibold text-red-700 mb-2">Nie potwierdzili ({confirmationsData[announcement._id].notConfirmedUsers.length}):</h5>
                                  <div className="space-y-1">
                                    {confirmationsData[announcement._id].notConfirmedUsers.map((item, index) => (
                                      <div key={index} className="flex items-center px-3 py-2 bg-red-50 rounded-lg">
                                        <span className="text-sm text-gray-700">
                                          {item.user.firstName && item.user.lastName
                                            ? `${item.user.firstName} ${item.user.lastName}`
                                            : item.user.username}
                                          {item.user.email && <span className="text-gray-500 ml-2">({item.user.email})</span>}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : confirmationStats ? (
                        <div className="text-sm text-gray-600 mt-2">
                          Kliknij "Pokaż szczegóły", aby zobaczyć listę użytkowników, którzy potwierdzili przeczytanie.
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600">
                          Kliknij "Pokaż szczegóły", aby zobaczyć listę użytkowników, którzy potwierdzili przeczytanie.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Komentarze */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2" />
                      Komentarze ({announcement.comments?.length || 0})
                    </h4>
                    
                    {/* Lista komentarzy */}
                    {announcement.comments && announcement.comments.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {announcement.comments.map((comment, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-sm text-gray-900">
                                  {comment.createdBy?.firstName && comment.createdBy?.lastName
                                    ? `${comment.createdBy.firstName} ${comment.createdBy.lastName}`
                                    : comment.createdBy?.username || 'Nieznany'}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {new Date(comment.createdAt).toLocaleString('pl-PL')}
                              </span>
                            </div>
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Formularz dodawania komentarza */}
                    <div className="flex space-x-2">
                      <textarea
                        value={commentTexts[announcement._id] || ''}
                        onChange={(e) => setCommentTexts({ ...commentTexts, [announcement._id]: e.target.value })}
                        placeholder="Napisz komentarz..."
                        rows={2}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                      />
                      <button
                        onClick={() => {
                          const text = commentTexts[announcement._id]?.trim();
                          if (text) {
                            addCommentMutation.mutate({
                              announcementId: announcement._id,
                              text: text
                            });
                          }
                        }}
                        disabled={!commentTexts[announcement._id]?.trim() || addCommentMutation.isLoading}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Akcje */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {readStatus.read && (
                        <div className="flex items-center text-green-600 text-sm">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span>Przeczytane</span>
                          {readStatus.readAt && (
                            <span className="ml-2 text-gray-500">
                              {new Date(readStatus.readAt).toLocaleDateString('pl-PL')}
                            </span>
                          )}
                        </div>
                      )}
                      {readStatus.confirmed && (
                        <div className="flex items-center text-green-600 text-sm">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span>Potwierdzone</span>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {!readStatus.read && (
                        <button
                          onClick={() => handleMarkAsRead(announcement._id)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          Oznacz jako przeczytane
                        </button>
                      )}
                      {needsConfirmation && (
                        <button
                          onClick={() => handleConfirmRead(announcement._id)}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Potwierdź zapoznanie się</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Announcements;

